/**
 * Token store — split by lifetime, not stored as one blob.
 *
 * Both tokens used to live in sessionStorage, which dies with the tab. The
 * reasoning was sound (a stolen token should not outlive the browser session),
 * but the effect was that every customer was signed out whenever they closed
 * the window — read, correctly, as "the session is far too short". The access
 * token's own 15-minute TTL was never the cause: `lib/api.js` renews it
 * silently on 401 and the refresh token is good for 30 days.
 *
 * So the two tokens are stored according to what they actually are:
 *
 *   access token  → sessionStorage. Short-lived (15 min) and used on every
 *                   request, so it is the one an XSS payload would grab. It
 *                   still dies with the tab, and the interceptor mints a new
 *                   one from the refresh token without the customer noticing.
 *   refresh token → localStorage. This is what makes the session survive a
 *                   browser restart. It is rotated on every use
 *                   (`POST /auth/refresh` revokes the old row), so a stolen
 *                   copy is invalidated the moment the real customer refreshes.
 *
 * The ideal design is still a backend-issued httpOnly cookie; that needs
 * cookie-based CORS and CSRF plumbing. This helper stays the single point where
 * the underlying store can be swapped without touching consumers.
 *
 * Cart id is intentionally kept in localStorage because it is not sensitive
 * and losing it degrades UX (guests would lose their cart on tab close).
 */

const ACCESS_KEY = "atelier_access_token";
const REFRESH_KEY = "atelier_refresh_token";

function _store(persistent) {
  if (typeof window === "undefined") return null;
  return persistent ? window.localStorage : window.sessionStorage;
}

function _get(key, persistent) {
  const store = _store(persistent);
  if (!store) return null;
  try { return store.getItem(key); }
  catch (err) { console.error("[atelier] token read failed", err); return null; }
}

function _set(key, value, persistent) {
  const store = _store(persistent);
  if (!store) return;
  try { store.setItem(key, value); }
  catch (err) { console.error("[atelier] token write failed", err); }
}

function _remove(key) {
  if (typeof window === "undefined") return;
  // Clear both stores: a session that predates this split still has its refresh
  // token in sessionStorage, and signing out must not leave either copy behind.
  for (const store of [window.localStorage, window.sessionStorage]) {
    try { store.removeItem(key); }
    catch (err) { console.error("[atelier] token remove failed", err); }
  }
}

export const authStore = {
  getAccessToken() { return _get(ACCESS_KEY, false); },
  getRefreshToken() {
    // Fall back to sessionStorage so a session opened before this change keeps
    // working instead of being logged out by the upgrade itself.
    return _get(REFRESH_KEY, true) || _get(REFRESH_KEY, false);
  },
  setTokens(access, refresh) {
    _set(ACCESS_KEY, access || "", false);
    if (refresh) _set(REFRESH_KEY, refresh, true);
  },
  clear() { _remove(ACCESS_KEY); _remove(REFRESH_KEY); },
};
